import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { TextField, CircularProgress, Popper, ClickAwayListener, Paper } from '@mui/material';
import { AttachFile, ArrowDropDown } from '@mui/icons-material';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import ReactDOM from "react-dom";
import Draggable from "react-draggable";

import CreateRelation from './prop/CreateRelation';
import ContactSelectionModal from './ContactSelectionModal';
import PendingAttachmentsList from '../Attachments/PendingAttachmentsList';
import IntegrationSelector from '../Integration/IntegrationSelector';
import ConnectionStatus from '../Integration/ConnectionStatus';
import { sendCommunication } from '../../store/Activities';
import { getIntegrationStatus } from '../../store/Integration';

const SendEmail = ({ clearActivityForm, onCreateActivity, isActiveExpande, setIsActiveExpande }) => {
  const dispatch = useDispatch();

  const { associations, activityForm, dropdowns, selectedContactEmail, communication } = useSelector(state => state['Activities']);
  const [localPendingAttachments, setLocalPendingAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const { singlePageData } = useSelector(state => state['SinglePage']);
  const emailConnection = communication?.['email'] || {};
  const isSending = emailConnection['sending'] ?? false;

  // Get connection status from Integration store (unified approach)
  const integrationStatus = useSelector(state => state['Integration']['integrationStatus']);
  const gmailStatus = integrationStatus?.gmail;
  const isCheckingConnection = gmailStatus?.loading ?? true;
  const hasConnection = gmailStatus?.data?.isConnected ?? false;

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [localAttachments, setLocalAttachments] = useState([]);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState('');
  const toFieldRef = useRef(null);

  // Debug: Log when integration ID changes
  useEffect(() => {
    console.log('[SendEmail] selectedIntegrationId changed to:', selectedIntegrationId);
  }, [selectedIntegrationId]);

  const [activeDrags, setActiveDrags] = useState(0);
  const [deltaPosition, setDeltaPosition] = useState({ x: 0, y: 0 });
  const [controlledPosition, setControlledPosition] = useState({ x: -400, y: 200 });

  useEffect(() => {
    dispatch(getIntegrationStatus('gmail'));

    // Pre-fill email if contact is selected from deal modal or individual contact
    if (selectedContactEmail) {
      setTo(selectedContactEmail);
    } else if (singlePageData && singlePageData['email']) {
      setTo(singlePageData['email']);
    } else {
      const availableContacts = getAvailableContacts();
      if (availableContacts.length === 1 && availableContacts[0]['email']) {
        const singleContact = availableContacts[0];
        setTo(singleContact['email']);
        setSelectedContacts([singleContact]);
      }
    }

    const subjectInput = document.getElementById('email-subject');
    if (subjectInput) subjectInput.focus();
  }, [singlePageData, selectedContactEmail, dispatch]);

  // Get available contacts based on context
  const getAvailableContacts = () => {
    // If we have contact associations, fetch those contacts
    if (associations?.['contacts']?.length > 0) {
      // For now, if we have specific contactIds, we might need to fetch them
      // This would require an API call or accessing them from Redux state
      return [];
    }

    // If we're on a deal/company page, use the page's contacts
    if ((associations?.['deals']?.length > 0 || associations?.['companies']?.length > 0) && singlePageData && singlePageData.contacts) {
      return singlePageData.contacts;
    }

    // If we're on a contact page, use that contact
    if (singlePageData && singlePageData.email) {
      return [singlePageData];
    }

    return [];
  };

  const handleContactSelection = (contacts) => {
    setSelectedContacts(contacts);
    const emails = contacts.filter(c => c['email']).map(c => c['email']).join(', ');
    setTo(emails);
    setShowContactDropdown(false);
  };

  // MIME type mapping for file extensions
  const getMimeTypeFromExtension = (filename) => {
    const extension = filename.toLowerCase().split('.').pop();
    const mimeTypes = {
      // Images
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'bmp': 'image/bmp',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
      'ico': 'image/x-icon',

      // Documents
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'rtf': 'application/rtf',
      'odt': 'application/vnd.oasis.opendocument.text',
      'ods': 'application/vnd.oasis.opendocument.spreadsheet',
      'odp': 'application/vnd.oasis.opendocument.presentation',

      // Text files
      'txt': 'text/plain',
      'csv': 'text/csv',
      'xml': 'text/xml',
      'html': 'text/html',
      'htm': 'text/html',
      'css': 'text/css',
      'js': 'text/javascript',
      'json': 'application/json',

      // Archives
      'zip': 'application/zip',
      'rar': 'application/x-rar-compressed',
      '7z': 'application/x-7z-compressed',
      'tar': 'application/x-tar',
      'gz': 'application/gzip',

      // Audio
      'mp3': 'audio/mpeg',
      'wav': 'audio/wav',
      'ogg': 'audio/ogg',
      'm4a': 'audio/mp4',
      'flac': 'audio/flac',

      // Video
      'mp4': 'video/mp4',
      'avi': 'video/x-msvideo',
      'mov': 'video/quicktime',
      'wmv': 'video/x-ms-wmv',
      'flv': 'video/x-flv',
      'webm': 'video/webm',

      // Other common formats
      'exe': 'application/x-msdownload',
      'deb': 'application/x-debian-package',
      'dmg': 'application/x-apple-diskimage',
      'iso': 'application/x-iso9660-image'
    };

    return mimeTypes[extension] || 'application/octet-stream';
  };

  // Helper function to convert File to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      // Check file size (25MB limit for Gmail API)
      const maxSize = 25 * 1024 * 1024; // 25MB in bytes
      if (file.size > maxSize) {
        reject(new Error(`File "${file.name}" is too large. Maximum size is 25MB.`));
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        try {
          // Extract base64 content from data URL
          const base64String = reader.result.split(',')[1];

          // Determine MIME type - use file.type if available, otherwise detect from extension
          let mimeType = file.type;
          if (!mimeType || mimeType === '') {
            mimeType = getMimeTypeFromExtension(file.name);
          }

          const attachment = {
            filename: file.name,
            content: base64String,
            mimeType: mimeType
          };

          resolve(attachment);
        } catch (error) {
          console.error(`[SendEmail] Error processing attachment ${file.name}:`, error);
          reject(new Error(`Failed to process attachment "${file.name}": ${error.message}`));
        }
      };
      reader.onerror = (error) => {
        console.error(`[SendEmail] FileReader error for ${file.name}:`, error);
        reject(new Error(`Failed to read file "${file.name}"`));
      };
    });
  };

  const handleSendEmail = async () => {
    if (!subject.trim()) return alert('Please enter a subject');
    if (!emailBody.trim()) return alert('Please enter a message');
    if (!to.trim()) return alert('Please enter a recipient email');

    // Convert attachments to base64
    let formattedAttachments = [];
    if (localPendingAttachments && localPendingAttachments.length > 0) {
      const attachmentPromises = localPendingAttachments.map((fileObj, index) => {
        const file = fileObj['file'] || fileObj;
        if (file instanceof File) return fileToBase64(file);
        if (fileObj && fileObj['filename'] && fileObj['content'] && fileObj['mimeType']) return Promise.resolve(fileObj);
        return Promise.reject(new Error(`Invalid file at position ${index + 1}`));
      });

      try {
        formattedAttachments = await Promise.all(attachmentPromises);
      } catch (error) {
        return alert(`Failed to process attachments: ${error.message}. Please check the files and try again.`);
      }
    }

    dispatch(sendCommunication({
      channel: 'email',
      associations, // Pass associations object directly
      to: to,
      cc: cc || undefined,
      bcc: bcc || undefined,
      subject: subject,
      body: emailBody,
      attachments: formattedAttachments,
      integrationId: selectedIntegrationId || undefined,
      successCallback: () => {
        setTo('');
        setCc('');
        setBcc('');
        setSubject('');
        setEmailBody('');
        setLocalPendingAttachments([]);
        clearActivityForm();
      }
    }));
  };

  const handleFileRemoved = (fileId) => {
    setLocalPendingAttachments(prev => prev.filter(f => f['id'] !== fileId));
  };

  const handleToolbarFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const maxSize = 25 * 1024 * 1024; // 25MB for email
    const maxFiles = 10;
    const validFiles = files.filter(file => file['size'] <= maxSize && file['size'] > 0);
    if (localPendingAttachments.length + validFiles.length > maxFiles) { alert(`Maximum ${maxFiles} files allowed.`); return; }
    const newFiles = validFiles.map(file => ({ id: Date.now() + '_' + Math.random().toString(36).substr(2, 9) + '_' + file['name'], name: file['name'], size: file['size'], type: file['type'] || '', lastModified: file['lastModified'], file: file }));
    setLocalPendingAttachments(prev => [...prev, ...newFiles]);
    e.target.value = '';
  };

  // ✅ Event Logger for Draggable
  const handleDrag = useCallback((e, ui) => {
    setDeltaPosition(prev => ({
      x: prev.x + ui.deltaX,
      y: prev.y + ui.deltaY,
    }));
  }, []);

  const onStart = () => setActiveDrags(d => d + 1);
  const onStop = () => setActiveDrags(d => d - 1);

  const onDrop = (e) => {
    setActiveDrags(d => d - 1);
    if (e.target.classList.contains("drop-target")) {
      alert("Dropped!");
      e.target.classList.remove("hovered");
    }
  };

  const onDropAreaMouseEnter = (e) => {
    if (activeDrags) e.target.classList.add("hovered");
  };
  const onDropAreaMouseLeave = (e) => {
    e.target.classList.remove("hovered");
  };

  const adjustXPos = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setControlledPosition(prev => ({ x: prev.x - 10, y: prev.y }));
  };

  const adjustYPos = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setControlledPosition(prev => ({ x: prev.x, y: prev.y - 10 }));
  };

  const onControlledDrag = (e, position) => {
    const { x, y } = position;
    setControlledPosition({ x, y });
  };

  const onControlledDragStop = (e, position) => {
    onControlledDrag(e, position);
    onStop();
  };

  const dragHandlers = { onStart, onStop };


  const editorModules = { toolbar: '#custom-toolbar', clipboard: { matchVisual: false } };
  const formats = ['font', 'size', 'color', 'background', 'align', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'link', 'image', 'align'];


  if (isCheckingConnection) {
    return (
      <div className={`add-note-sec email-single-box ${isActiveExpande ? 'expande-box' : ''}`}>
        <div className="add-note-box">
          <div className="add-note-header">
            <div className="left-area">
              <h4>Send Email</h4>
            </div>
            <div className="right-area">
              <button className="close-btns" onClick={() => setIsActiveExpande(prev => !prev)}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.45508 7.9541L2.69886 11.8543L6.59902 11.6105" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M7.64551 2.07227L11.5457 1.82855L11.7895 5.72871" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M11.5444 1.82812L2.69819 11.8538" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button className="close-btns" onClick={() => { clearActivityForm(); setIsActiveExpande(false); }}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)" />
                </svg>
              </button>
            </div>
          </div>
          <div className="bottom-note-box">
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
              <CircularProgress size={24} />
              <span style={{ marginLeft: '10px' }}>Checking email connection...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasConnection) {
    return (
      <div className={`add-note-sec email-single-box ${isActiveExpande ? 'expande-box' : ''}`}>
        <div className="add-note-box">
          <div className="add-note-header">
            <div className="left-area">
              <h4>Send Email</h4>
            </div>
            <div className="right-area">
              <button className="close-btns" onClick={() => setIsActiveExpande(prev => !prev)}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.45508 7.9541L2.69886 11.8543L6.59902 11.6105" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M7.64551 2.07227L11.5457 1.82855L11.7895 5.72871" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M11.5444 1.82812L2.69819 11.8538" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button className="close-btns" onClick={() => { clearActivityForm(); setIsActiveExpande(false); }}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)" />
                </svg>
              </button>
            </div>
          </div>
          <div className="bottom-note-box">
            <div className="celander-box">
              <h4>
                <i className='icon'>
                  <svg width="16" height="13" viewBox="0 0 16 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.6 0.600098H3.40002C1.86002 0.600098 0.616016 1.8521 0.616016 3.3841L0.608016 10.2161C0.608016 11.7481 1.86002 13.0001 3.40002 13.0001H12.6C14.14 13.0001 15.4 11.7481 15.4 10.2161V3.3841C15.4 1.8521 14.14 0.600098 12.6 0.600098ZM12.32 4.4401L8.41601 7.0561C8.16001 7.2281 7.84001 7.2281 7.58401 7.0561L3.68002 4.4401C3.55602 4.3601 3.45602 4.2481 3.38802 4.1161C3.32002 3.9841 3.28802 3.8361 3.29602 3.6881C3.30401 3.5401 3.35201 3.3961 3.43201 3.2721C3.51201 3.1481 3.62402 3.0481 3.75602 2.9841C3.88001 2.9161 4.02002 2.8841 4.16002 2.8921C4.30001 2.9001 4.43601 2.9481 4.55201 3.0281L8.00001 5.3761L11.448 3.0281C11.564 2.9481 11.7 2.9001 11.84 2.8921C11.98 2.8841 12.12 2.9161 12.244 2.9841C12.376 3.0481 12.488 3.1481 12.568 3.2721C12.648 3.3961 12.696 3.5401 12.704 3.6881C12.712 3.8361 12.68 3.9841 12.612 4.1161C12.544 4.2481 12.444 4.3601 12.32 4.4401Z" fill="#4B5563" />
                  </svg>
                </i>
                Connect Gmail to Send Emails
              </h4>
              <p>Get notified when people open & click your emails, and send emails straight from CRM.</p>

              {/* Use ConnectionStatus component in compact mode */}
              <ConnectionStatus
                toolName="gmail"
                compact={true}
                onConnect={() => {
                  console.log('[SendEmail] Gmail connection initiated');
                  // Connection check will be triggered by gmail-connected event listener
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Draggable
      {...dragHandlers}
      bounds="body" // ✅ allow dragging across the full page
      handle=".add-note-header" // optional: drag only by header
      cancel=".MuiSelect-root, .MuiMenuItem-root, .MuiMenu-root, .MuiPaper-root, .MuiList-root, .MuiFormControl-root, .MuiPopover-root, input, textarea, button, .ql-editor" // Don't drag these elements
    >
      <div className={`add-note-sec email-single-box ${isActiveExpande ? 'expande-box' : ''}`}>
        <div className="add-note-box">
          <div className="add-note-header">
            <div className="left-area">
              <h4>Send Email</h4>
            </div>
            <div className="right-area">
              <button className="close-btns" onClick={() => setIsActiveExpande(prev => !prev)}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.45508 7.9541L2.69886 11.8543L6.59902 11.6105" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M7.64551 2.07227L11.5457 1.82855L11.7895 5.72871" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M11.5444 1.82812L2.69819 11.8538" stroke="var(--primary-color)" strokeWidth="1.11421" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button className="close-btns" onClick={() => { clearActivityForm(); setIsActiveExpande(false); }}>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.21204 0.303865C8.6172 -0.100965 9.28744 -0.101115 9.69251 0.303865C10.0977 0.709019 10.0977 1.38016 9.69251 1.78531L6.47962 4.9982L9.69251 8.21109C10.0977 8.61625 10.0977 9.28738 9.69251 9.69254C9.48301 9.902 9.21762 10.0001 8.95227 10.0002C8.68695 10.0002 8.42155 9.9019 8.21204 9.69254L4.99817 6.47867L1.78528 9.69254C1.58981 9.90197 1.31035 10 1.04504 10.0002C0.779599 10.0002 0.513396 9.9021 0.303834 9.69254C-0.101246 9.28738 -0.101295 8.61622 0.303834 8.21109L3.51672 4.9982L0.303834 1.78531C-0.101299 1.38018 -0.101257 0.709025 0.303834 0.303865C0.708987 -0.101288 1.38013 -0.101288 1.78528 0.303865L4.99817 3.51676L8.21204 0.303865Z" fill="var(--primary-color)" />
                </svg>
              </button>
            </div>
          </div>
          <div className="bottom-note-box">
            {/* Integration Selector */}
            <div className="group-form">
              <label>Send From</label>
              <IntegrationSelector
                toolName="Gmail"
                value={selectedIntegrationId}
                onChange={setSelectedIntegrationId}
                placeholder="Select Gmail account to send from"
              />
            </div>

            {/* Email Fields */}
            <div className="group-form" style={{ position: 'relative' }}>
              <label>To</label>
              <div style={{ position: 'relative' }}>
                <TextField
                  ref={toFieldRef}
                  id="email-to"
                  type="email"
                  fullWidth
                  size="small"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  onClick={() => {
                    const contacts = getAvailableContacts();
                    if (contacts.length > 1) {
                      setShowContactDropdown(true);
                    }
                  }}
                  placeholder={getAvailableContacts().length > 1 ? "Click to select contacts or type email addresses" : "Type email address"}
                  variant="outlined"
                  InputProps={{
                    endAdornment: getAvailableContacts().length > 1 ? (
                      <ArrowDropDown
                        style={{ cursor: 'pointer', color: '#6b7280' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowContactDropdown(!showContactDropdown);
                        }}
                      />
                    ) : null
                  }}
                />

                {/* Contact Selection Dropdown */}
                <Popper
                  open={showContactDropdown}
                  anchorEl={toFieldRef.current}
                  placement="bottom-start"
                  style={{ zIndex: 1300, width: toFieldRef.current?.offsetWidth || 'auto' }}
                >
                  <ClickAwayListener onClickAway={() => setShowContactDropdown(false)}>
                    <Paper elevation={8} style={{ marginTop: '4px', border: '1px solid #e5e7eb' }}>
                      <ContactSelectionModal
                        contacts={getAvailableContacts()}
                        initialSelectedContacts={selectedContacts}
                        onSelectContacts={handleContactSelection}
                        onClose={() => setShowContactDropdown(false)}
                      />
                    </Paper>
                  </ClickAwayListener>
                </Popper>
              </div>
            </div>

            <div className="group-form">
              <label>CC (optional)</label>
              <TextField
                type="text"
                fullWidth
                size="small"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="cc@example.com"
                variant="outlined"
              />
            </div>

            <div className="group-form">
              <label>BCC (optional)</label>
              <TextField
                type="text"
                fullWidth
                size="small"
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
                placeholder="bcc@example.com"
                variant="outlined"
              />
            </div>

            <div className="group-form">
              <label>Subject</label>
              <TextField
                id="email-subject"
                type="text"
                fullWidth
                size="small"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter subject"
                variant="outlined"
              />
            </div>
            <div className="group-form">
              <label class="text-capitalize">Message</label>
              <div className="editor-wrapper">
                <ReactQuill
                  formats={formats}
                  modules={editorModules}
                  bounds=".editor-wrapper"
                  theme="snow" // must be snow for custom toolbar
                  onChange={(value) => setEmailBody(value)}
                  value={emailBody}
                  placeholder="Start typing to leave a note..."
                />

                {/* Custom Toolbar - MUST BE visible */}
                <div id="custom-toolbar" className="bottom-toolbar">
                  <span className="ql-formats">
                    <button className="ql-bold" />
                    <button className="ql-italic" />
                    <button className="ql-underline" />
                    <button className="ql-strike" />
                  </span>
                  <div className="ql-formats more-dropdown">
                    <button className="more-toggle" onClick={() => toggleDropdown('showMore')} >More ▾</button>
                    <span
                      className="more-dropdown-content"
                      onClick={(e) => e.stopPropagation()}
                      style={{ display: dropdowns.showMore ? 'block' : 'none' }}

                    >
                      <svg data-test-id="popover-arrow" fill="#FFFFFF" aria-hidden="true" width="30" height="28" viewBox="0 0 28 28" style={{ position: 'absolute', pointerEvents: 'none', top: '100%', left: '7.7185px' }}><path clip-path="url(#:ra5:)" fill="none" stroke="#CBD6E2" stroke-width="3" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><path stroke="#FFFFFF" d="M0,0 H28 L14,14 Q14,14 14,14 Z"></path><clipPath id=":ra5:"><rect x="-1" y="1" width="30" height="28"></rect></clipPath></svg>
                      <span className="ql-formats" onClick={(e) => e.stopPropagation()}>
                        <select className="ql-font" />
                        <select className="ql-size" />
                        <select className="ql-color" />
                        <select className="ql-background" />
                        <button className="ql-blockquote" />
                        <button className="ql-list" value="ordered" />
                        <select className="ql-align" />
                      </span>
                    </span>
                  </div>
                  <span className="ql-formats">
                    <button className="ql-list" value="ordered" />
                    <button className="ql-list" value="bullet" />
                  </span>
                  <span className="ql-formats">
                    <button className="ql-link" />
                    <button className="ql-image" />
                  </span>
                  <span className="ql-formats">
                    <button type="button" className="ql-attachment" onClick={() => fileInputRef.current?.click()}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4C9.11 4 6.6 5.64 5.35 8.04C2.34 8.36 0 10.91 0 14C0 17.31 2.69 20 6 20H19C21.76 20 24 17.76 24 15C24 12.36 21.95 10.22 19.35 10.04ZM14 13V17H10V13H7L12 8L17 13H14Z" fill="currentColor"/>
                      </svg>
                    </button>
                    <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleToolbarFileSelect} />
                  </span>
                </div>
              </div>

            </div>

            {/* Attachments List */}
            <PendingAttachmentsList files={localPendingAttachments} onRemove={handleFileRemoved} />

            {/* Related objects */}
            <CreateRelation
              type="new"
              initialAssociations={associations}
            />
          </div>
          <div className="add-note-footer">
            <button
              className='btn-style-new'
              onClick={handleSendEmail}
              disabled={isSending || !to.trim() || !subject.trim() || !emailBody.trim()}
            >
              {isSending ? 'Sending...' : 'Send Email'}
            </button>
          </div>
        </div>
      </div>
    </Draggable>
  );
};


function RemWrapper({ children, remBaseline = 16, style }) {
  const translateTransformToRem = (transform, remBaseline = 16) => {
    const convertedValues = transform
      .replace("translate(", "")
      .replace(")", "")
      .split(",")
      .map(px => px.replace("px", ""))
      .map(px => parseInt(px, 10) / remBaseline)
      .map(x => `${x}rem`);

    const [x, y] = convertedValues;
    return `translate(${x}, ${y})`;
  };

  const child = React.Children.only(children);
  const editedStyle = {
    ...child.props.style,
    ...style,
    transform: style?.transform
      ? translateTransformToRem(style.transform, remBaseline)
      : undefined,
  };

  return React.cloneElement(child, {
    ...child.props,
    style: editedStyle,
  });
}

export default SendEmail;